import { Box, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { MoreHoriz } from '@tempo-io/sui-icons';
import { useContext } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  RowOffsetContext,
  useExportMode,
  useSwimlaneRootProps,
} from '@/components/ui/swimlane/context';
import {
  AXIS_HEIGHT,
  COLUMN_HEADER_HEIGHT,
  GRID_GAP,
} from '@/components/ui/swimlane/layout/constants';
import { HeaderCell } from '../HeaderCell';
import { SwimlaneHeaderMenu } from '../menu/SwimlaneHeaderMenu';
import type { IHeaderProps } from '../types';
import { HorizontalTitle } from './HorizontalHeader';

export type RowHeaderProps = IHeaderProps & React.HTMLAttributes<HTMLDivElement>;

const HEADER_MENU_HEIGHT = 40;

// Consider refactoring the SL and headers to properly pass the config instead of requiring the consumer to redefine this component
export function RowHeader({
  backgroundColor = 'transparent',
  labelColor = 'inherit',
  children = '',
  onOpenCard,
  hideHeader,
  openCardLabel = '',
  hideHeaderTooltip,
  reorderDisabledTooltip,
  isReOrderingDisabled = false,
  isViewCardOptionDisabled = false,
  isGroupHeader,
  parentHeaderId,
  forceVertical = false,
  ...containerProps
}: RowHeaderProps & { forceVertical?: boolean }) {
  const { ref: bottomRef, entry: bottomEntry } = useInView();
  const { offset, height } = useContext(RowOffsetContext);

  const {
    rowAxisLabelText,
    rowHeaderOrientation: contextOrientation,
    isPublicationMode,
    isHeadersSticky,
    isHeaderMenuDisabled,
  } = useSwimlaneRootProps();
  const { isExporting } = useExportMode();

  const rowHeaderOrientation = forceVertical ? 'vertical' : contextOrientation;

  // Backwards compatibility: if isHeaderMenuDisabled is not passed, only use isPublicationMode
  const shouldHideMenu = isHeaderMenuDisabled !== undefined ? isHeaderMenuDisabled : false;

  const columnHeaderOffset =
    typeof rowAxisLabelText === 'string'
      ? AXIS_HEIGHT.value + GRID_GAP.value + COLUMN_HEADER_HEIGHT.value
      : COLUMN_HEADER_HEIGHT.value;

  return (
    <RowHeaderCell
      data-export="row-header"
      role="rowheader"
      $backgroundColor={backgroundColor}
      $labelColor={labelColor}
      {...containerProps}
    >
      {/* The calc keeps the title and button in view while scrolling */}
      <HeaderPositioner
        isHeaderSticky={isHeadersSticky && !isExporting}
        isPublicationMode={isPublicationMode}
        style={{
          top: isHeadersSticky && !isExporting ? `calc(-${offset}px + ${columnHeaderOffset}px)` : 0,
        }}
      >
        {!isPublicationMode && !shouldHideMenu && (
          <SwimlaneHeaderMenu
            Icon={MoreHoriz}
            onOpenCard={onOpenCard}
            hideHeader={hideHeader}
            openCardLabel={openCardLabel}
            hideHeaderTooltip={hideHeaderTooltip}
            reorderDisabledTooltip={reorderDisabledTooltip}
            isReOrderingDisabled={isReOrderingDisabled}
            isViewCardOptionDisabled={isViewCardOptionDisabled}
            isHeaderMenuDisabled={isHeaderMenuDisabled}
            isGroupHeader={isGroupHeader}
            parentHeaderId={parentHeaderId}
            level={2}
          />
        )}
        {rowHeaderOrientation === 'vertical' ? (
          <Tooltip title={typeof children === 'string' ? children : ''} followCursor>
            <VStyledTitle
              className="fs-exclude"
              data-dd-action-name="swimlane label - redacted"
              data-testid="vertical"
              isHeadersSticky={isHeadersSticky}
              isExporting={isExporting}
              style={{
                maxHeight: `${height}px`,
                padding: '12px 0',
              }}
            >
              {children}
            </VStyledTitle>
          </Tooltip>
        ) : (
          <Box style={{ padding: '12px 0' }}>
            <HorizontalTitle
              menuHeight={HEADER_MENU_HEIGHT}
              bottomEntry={bottomEntry}
              columnHeaderOffset={columnHeaderOffset}
            >
              {children}
            </HorizontalTitle>
          </Box>
        )}
      </HeaderPositioner>

      <span
        ref={bottomRef}
        style={{ height: '1px', width: '100%', position: 'fixed', bottom: 0, marginTop: '16px' }}
      />
    </RowHeaderCell>
  );
}

const RowHeaderCell = styled(HeaderCell)({
  flexDirection: 'column',
  height: '100%',
  width: 'inherit',
});

const HeaderPositioner = styled('div')<{ isPublicationMode: boolean; isHeaderSticky: boolean }>(
  ({ isHeaderSticky }) => ({
    position: isHeaderSticky ? 'sticky' : 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'end',
    width: 'inherit',
  }),
);

const VStyledTitle = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isHeadersSticky' && prop !== 'isExporting',
})<{
  isHeadersSticky?: boolean;
  isExporting?: boolean;
}>(({ isHeadersSticky, isExporting }) => ({
  overflow: 'hidden',
  paddingTop: '12px',
  position: isHeadersSticky && !isExporting ? 'sticky' : 'relative',
  textOverflow: 'ellipsis',
  transform: 'rotate(180deg)',
  whiteSpace: 'nowrap',
  width: 'inherit',
  writingMode: 'vertical-lr',
  fontSize: '0.875rem',
  fontWeight: 600,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
}));
